import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import { DragHandleRenderProps, SortableItemProps } from './sortable-list.types';

/**
 * SortableItem - Individual draggable item within SortableList
 *
 * This component handles the @dnd-kit integration and passes drag handle props
 * to the children render function. The children are responsible for rendering
 * the actual content and applying the drag handle where appropriate.
 *
 * @example
 * ```tsx
 * <SortableItem id="item-1">
 *   {(dragHandle) => (
 *     <div ref={dragHandle.ref}>
 *       <IconDrag {...dragHandle.listeners} {...dragHandle.attributes} />
 *       <span>Item content</span>
 *     </div>
 *   )}
 * </SortableItem>
 * ```
 */
export const SortableItem = ({ id, children, className, disabled }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Prepare drag handle props to pass to children
  const dragHandleProps: DragHandleRenderProps = {
    listeners,
    attributes,
    ref: setNodeRef,
    isDragging,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(className, {
        'cursor-not-allowed': disabled,
      })}>
      {/* Call children as function, passing drag handle props */}
      {children(dragHandleProps)}
    </div>
  );
};
